import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Collapsible, CollapsibleContent } from '../../components/ui/collapsible';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  Package, Truck, ChevronDown, ChevronUp,
  BarChart3, Layers, AlertCircle
} from 'lucide-react';
import { formatNumber, formatMaterialMap } from '../../lib/utils';
import DashboardFilters from './DashboardFilters';
import DrillDownModal from './DrillDownModal';
import SmartAlerts from './SmartAlerts';
import { useCompany } from '../../contexts/CompanyContext';

const ItemCollapsible = ({ item, onDrilldown }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [qOpen, setQOpen] = useState(true);
  const [fqOpen, setFqOpen] = useState(true);
  const overview = item.order_overview;
  const lr = item.lr_status;
  const lrQty = item.lr_quantities || {};
  const quantities = item.quantity_summary;

  return (
    <div data-testid={`item-section-${item.item_name}`} className="border border-slate-200 rounded-lg mb-4 bg-white overflow-hidden">
      <button type="button" onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors">
        <div className="flex items-center gap-3">
          <Package className="h-5 w-5 text-slate-500" />
          <span className="font-semibold text-slate-900 text-base">{item.item_name}</span>
          <Badge variant="outline" className="text-xs">{overview.total} orders</Badge>
        </div>
        <div className="flex items-center gap-4">
          <div className="hidden md:flex items-center gap-3 text-xs">
            {overview.pending > 0 && <span className="text-red-600 font-medium">{overview.pending} Pending</span>}
            {overview.partially_billed > 0 && <span className="text-amber-600 font-medium">{overview.partially_billed} Partial</span>}
            {overview.completed > 0 && <span className="text-teal-600 font-medium">{overview.completed} Done</span>}
          </div>
          <div className="text-slate-400">{isOpen ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}</div>
        </div>
      </button>

      <Collapsible open={isOpen}>
        <CollapsibleContent>
          <div className="p-4 border-t border-slate-100 space-y-5">
            {/* Quantity Summary - Primary section */}
            {Object.keys(quantities).length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Layers className="h-4 w-4" /> Quantity Summary
                </h4>
                {Object.entries(quantities).map(([unit, qty]) => (
                  <div key={unit} className="mb-3 last:mb-0">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">{unit}</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div data-testid={`item-qty-ordered-${item.item_name}-${unit}`} className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                        <p className="text-xs text-slate-500 mb-1">Ordered</p>
                        <p className="text-lg font-bold text-slate-900">{formatNumber(qty.ordered)}</p>
                      </div>
                      <div data-testid={`item-qty-billed-${item.item_name}-${unit}`} className="bg-white p-3 rounded-lg border border-slate-200">
                        <p className="text-xs text-slate-500 mb-1">Billed</p>
                        <p className="text-lg font-bold text-slate-900">{formatNumber(qty.billed)}</p>
                      </div>
                      <div data-testid={`item-qty-received-${item.item_name}-${unit}`} className="bg-teal-50 p-3 rounded-lg border border-teal-200">
                        <p className="text-xs text-teal-600 mb-1">Received</p>
                        <p className="text-lg font-bold text-teal-700">{formatNumber(qty.received)}</p>
                      </div>
                      <div data-testid={`item-qty-pending-${item.item_name}-${unit}`} className="bg-red-50 p-3 rounded-lg border border-red-200">
                        <p className="text-xs text-red-600 mb-1">Pending</p>
                        <p className="text-lg font-bold text-red-700">{formatNumber(qty.pending)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* LR Quantity Status */}
            <div>
              <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <Truck className="h-4 w-4" /> LR Status (Bales &amp; Material)
              </h4>
              <div className="grid grid-cols-3 gap-3">
                <div data-testid={`item-lr-transport-${item.item_name}`}
                  className="bg-blue-50 p-3 rounded-lg border border-blue-200 cursor-pointer hover:bg-blue-100 transition-colors"
                  onClick={() => onDrilldown('lr_in_transport', item.item_name)}>
                  <p className="text-xs text-blue-600 mb-1">In Transport</p>
                  <p className="text-xl font-bold text-blue-700">{formatNumber(lrQty.in_transport?.bales || 0)} <span className="text-xs font-medium">Bales</span></p>
                  <p className="text-xs text-blue-600 mt-1">{formatMaterialMap(lrQty.in_transport?.material)}</p>
                  <p className="text-xs text-blue-500 mt-1">{lr.in_transport} LRs</p>
                </div>
                <div data-testid={`item-lr-issued-${item.item_name}`}
                  className="bg-amber-50 p-3 rounded-lg border border-amber-200 cursor-pointer hover:bg-amber-100 transition-colors"
                  onClick={() => onDrilldown('lr_issued', item.item_name)}>
                  <p className="text-xs text-amber-600 mb-1">Issued for Delivery</p>
                  <p className="text-xl font-bold text-amber-700">{formatNumber(lrQty.issued_for_delivery?.bales || 0)} <span className="text-xs font-medium">Bales</span></p>
                  <p className="text-xs text-amber-600 mt-1">{formatMaterialMap(lrQty.issued_for_delivery?.material)}</p>
                  <p className="text-xs text-amber-500 mt-1">{lr.issued_for_delivery} LRs</p>
                </div>
                <div data-testid={`item-lr-delivered-${item.item_name}`}
                  className="bg-green-50 p-3 rounded-lg border border-green-200 cursor-pointer hover:bg-green-100 transition-colors"
                  onClick={() => onDrilldown('lr_delivered', item.item_name)}>
                  <p className="text-xs text-green-600 mb-1">Delivered</p>
                  <p className="text-xl font-bold text-green-700">{formatNumber(lrQty.delivered?.bales || 0)} <span className="text-xs font-medium">Bales</span></p>
                  <p className="text-xs text-green-600 mt-1">{formatMaterialMap(lrQty.delivered?.material)}</p>
                  <p className="text-xs text-green-500 mt-1">{lr.delivered} LRs</p>
                </div>
              </div>
            </div>

            {/* Order Overview */}
            <div>
              <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <BarChart3 className="h-4 w-4" /> Order Overview
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div data-testid={`item-total-${item.item_name}`}
                  className="bg-slate-50 p-3 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => onDrilldown('total_orders', item.item_name)}>
                  <p className="text-xs text-slate-500 mb-1">Total Orders</p>
                  <p className="text-xl font-bold text-slate-900">{overview.total}</p>
                </div>
                <div className="bg-red-50 p-3 rounded-lg border border-red-200 cursor-pointer hover:bg-red-100 transition-colors"
                  onClick={() => onDrilldown('pending_orders', item.item_name)}>
                  <p className="text-xs text-red-600 mb-1">Pending</p>
                  <p className="text-xl font-bold text-red-700">{overview.pending}</p>
                </div>
                <div className="bg-amber-50 p-3 rounded-lg border border-amber-200 cursor-pointer hover:bg-amber-100 transition-colors"
                  onClick={() => onDrilldown('partially_billed', item.item_name)}>
                  <p className="text-xs text-amber-600 mb-1">Partially Billed</p>
                  <p className="text-xl font-bold text-amber-700">{overview.partially_billed}</p>
                </div>
                <div className="bg-teal-50 p-3 rounded-lg border border-teal-200 cursor-pointer hover:bg-teal-100 transition-colors"
                  onClick={() => onDrilldown('completed_orders', item.item_name)}>
                  <p className="text-xs text-teal-600 mb-1">Completed</p>
                  <p className="text-xl font-bold text-teal-700">{overview.completed}</p>
                </div>
              </div>
            </div>

            {/* Quality Breakdown */}
            {item.quality_breakdown?.length > 0 && (
              <div>
                <button type="button" data-testid={`quality-breakdown-toggle-${item.item_name}`}
                  onClick={() => setQOpen(!qOpen)}
                  className="w-full flex items-center justify-between text-sm font-semibold text-slate-700 mb-3 hover:text-slate-900 transition-colors">
                  <span>Quality-wise Breakdown</span>
                  {qOpen ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                </button>
                {qOpen && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50">
                        <th className="text-left py-2 px-3 font-semibold text-slate-600">Quality</th>
                        <th className="text-right py-2 px-3 font-semibold text-slate-600">Orders</th>
                        <th className="text-right py-2 px-3 font-semibold text-slate-600">Ordered</th>
                        <th className="text-right py-2 px-3 font-semibold text-blue-600">Available In Transport</th>
                        <th className="text-right py-2 px-3 font-semibold text-slate-600">Received</th>
                      </tr>
                    </thead>
                    <tbody>
                      {item.quality_breakdown.map((q) => (
                        <tr key={q.quality_name} className="border-b border-slate-100">
                          <td className="py-2 px-3 font-medium text-slate-800">{q.quality_name}</td>
                          <td className="py-2 px-3 text-right text-slate-600">{q.order_count}</td>
                          <td className="py-2 px-3 text-right font-medium">{formatNumber(q.ordered)}</td>
                          <td data-testid={`quality-in-transit-${q.quality_name}`} className="py-2 px-3 text-right text-blue-700">
                            {Object.keys(q.in_transport_material || {}).length > 0
                              ? formatMaterialMap(q.in_transport_material)
                              : <span className="text-slate-400">—</span>}
                          </td>
                          <td className="py-2 px-3 text-right text-teal-700">{formatNumber(q.received)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                )}
              </div>
            )}

            {/* Finish Quality Breakdown */}
            {item.subquality_breakdown?.length > 0 && item.subquality_breakdown.some(sq => sq.subquality_name !== 'Not Specified') && (
              <div>
                <button type="button" data-testid={`finish-breakdown-toggle-${item.item_name}`}
                  onClick={() => setFqOpen(!fqOpen)}
                  className="w-full flex items-center justify-between text-sm font-semibold text-slate-700 mb-3 hover:text-slate-900 transition-colors">
                  <span>Finish Quality Breakdown</span>
                  {fqOpen ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                </button>
                {fqOpen && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50">
                        <th className="text-left py-2 px-3 font-semibold text-slate-600">Finish Quality</th>
                        <th className="text-right py-2 px-3 font-semibold text-slate-600">Orders</th>
                        <th className="text-right py-2 px-3 font-semibold text-slate-600">Ordered</th>
                        <th className="text-right py-2 px-3 font-semibold text-blue-600">Available In Transport</th>
                        <th className="text-right py-2 px-3 font-semibold text-slate-600">Received</th>
                      </tr>
                    </thead>
                    <tbody>
                      {item.subquality_breakdown.map((sq) => (
                        <tr key={sq.subquality_name} className="border-b border-slate-100">
                          <td className="py-2 px-3 font-medium text-slate-800">{sq.subquality_name}</td>
                          <td className="py-2 px-3 text-right text-slate-600">{sq.order_count}</td>
                          <td className="py-2 px-3 text-right font-medium">{formatNumber(sq.ordered)}</td>
                          <td data-testid={`finish-in-transit-${sq.subquality_name}`} className="py-2 px-3 text-right text-blue-700">
                            {Object.keys(sq.in_transport_material || {}).length > 0
                              ? formatMaterialMap(sq.in_transport_material)
                              : <span className="text-slate-400">—</span>}
                          </td>
                          <td className="py-2 px-3 text-right text-teal-700">{formatNumber(sq.received)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                )}
              </div>
            )}

            {/* Per-item Smart Alerts — placed last in the item's section */}
            <SmartAlerts alerts={item.smart_alerts} itemName={item.item_name} />
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

const ItemWiseDashboard = ({ filters, setFilters, filterOptions }) => {
  const { activeCompany } = useCompany();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [drilldown, setDrilldown] = useState({ open: false, data: null, loading: false });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const res = await api.get(`/dashboard/itemwise-data?${params.toString()}`);
      setData(res.data);
    } catch {
      toast.error('Failed to load item-wise data');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchData(); }, [fetchData, activeCompany?.company_id]);

  const openDrilldown = async (metric, itemName) => {
    setDrilldown({ open: true, data: null, loading: true });
    try {
      const params = new URLSearchParams({ metric });
      if (itemName) params.append('item_name', itemName);
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const res = await api.get(`/dashboard/drilldown?${params.toString()}`);
      setDrilldown({ open: true, data: res.data, loading: false });
    } catch {
      toast.error('Failed to load drill-down data');
      setDrilldown({ open: false, data: null, loading: false });
    }
  };

  if (loading && !data) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-slate-200 rounded w-1/4"></div>
        <div className="space-y-4">
          {[1, 2, 3].map(i => <div key={i} className="h-20 bg-slate-200 rounded"></div>)}
        </div>
      </div>
    );
  }

  const items = data?.items || [];

  return (
    <>
      <DashboardFilters filters={filters} setFilters={setFilters} filterOptions={filterOptions} showFinishQuality showLRStatus />

      {/* Item Sections */}
      {items.length > 0 ? (
        <div>
          <h3 className="text-base font-semibold text-slate-800 mb-4">Items ({items.length})</h3>
          {items.map((item) => (
            <ItemCollapsible key={item.item_name} item={item} onDrilldown={openDrilldown} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-slate-400">
          <AlertCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>No items found matching the current filters</p>
        </div>
      )}

      <DrillDownModal
        open={drilldown.open}
        onClose={() => setDrilldown({ open: false, data: null, loading: false })}
        data={drilldown.data}
        loading={drilldown.loading}
      />
    </>
  );
};

export default ItemWiseDashboard;
